import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { list_pages, create_page, update_page, delete_page, get_page } from '../../api/functions';
import { useApi } from '../../api/api';
import { LoadingSpinner, AdminEndpoint } from '../../components';
import PageDetailView from './PageDetailView';
import './TabContent.css';

const InfoPagesTab = () => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id;
  const listPagesApi = useApi(list_pages);
  const createPageApi = useApi(create_page);
  const updatePageApi = useApi(update_page);
  const deletePageApi = useApi(delete_page);
  const getPageApi = useApi(get_page);
  const gridRef = useRef(null);

  const [editingPage, setEditingPage] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allPages, setAllPages] = useState([]);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(16);

  const BATCH_SIZE = 50;
  const ROWS_PER_PAGE = 4;

  const fetchPages = useCallback(async () => {
    const params = {
      skip: 0,
      limit: BATCH_SIZE,
      show_private: true,
      search_query: searchQuery || ''
    };

    const data = await listPagesApi.execute(params);

    if (data) {
      setAllPages(data);
      setHasMorePages(data.length === BATCH_SIZE);
      setCurrentPage(1);
    }
  }, [searchQuery]);

  const loadMorePages = useCallback(async () => {
    if (!hasMorePages || listPagesApi.loading) return;

    const params = {
      skip: allPages.length,
      limit: BATCH_SIZE,
      show_private: true,
      search_query: searchQuery || ''
    };

    const data = await listPagesApi.execute(params);

    if (data && data.length > 0) {
      setAllPages([...allPages, ...data]);
      setHasMorePages(data.length === BATCH_SIZE);
    } else {
      setHasMorePages(false);
    }
  }, [allPages, hasMorePages, searchQuery, listPagesApi.loading]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new') || currentPath.includes('/new/');
    const isEditingPath = currentPath.includes('/edit');
    const isDetailPath = id && !isEditingPath && !isCreatingPath;

    if (!isCreatingPath && !isEditingPath && !isDetailPath) {
      fetchPages();
    }
  }, [fetchPages]);

  useEffect(() => {
    const calculateColumns = () => {
      if (gridRef.current) {
        const containerWidth = gridRef.current.offsetWidth;
        const minCardWidth = 280;
        const gap = 24;
        const columns = Math.max(1, Math.floor((containerWidth + gap) / (minCardWidth + gap)));
        const newItemsPerPage = ROWS_PER_PAGE * columns;
        setItemsPerPage(newItemsPerPage);
      }
    };

    calculateColumns();
    window.addEventListener('resize', calculateColumns);
    return () => window.removeEventListener('resize', calculateColumns);
  }, []);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new');
    const isEditingPath = currentPath.includes('/edit');

    if (isCreatingPath) {
      setIsCreating(true);
      setEditingPage(null);
    } else if (id && isEditingPath) {
      const fetchPageForEdit = async () => {
        const pageItem = await getPageApi.execute({ page_id: parseInt(id) });
        if (pageItem) {
          setEditingPage(pageItem);
          setIsCreating(false);
        }
      };
      fetchPageForEdit();
    } else {
      setIsCreating(false);
      setEditingPage(null);
    }
  }, [id, params]);

  useEffect(() => {
    const neededPages = currentPage * itemsPerPage;
    if (neededPages > allPages.length && hasMorePages && !listPagesApi.loading) {
      loadMorePages();
    }
  }, [currentPage, itemsPerPage, allPages.length, hasMorePages, listPagesApi.loading, loadMorePages]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedPages = allPages.slice(startIndex, endIndex);
  const totalPages = Math.ceil(allPages.length / itemsPerPage);

  const handleCreate = () => {
    navigate('/admin/infopages/new');
  };

  const handleEdit = (pageItem) => {
    navigate(`/admin/infopages/${pageItem.id}`);
  };

  const handleCancel = () => {
    navigate('/admin/infopages');
  };

  const storageKey = isCreating ? 'admin_infopages_create' : (id ? `admin_infopages_edit_${id}` : null);

  const handleCopy = (pageItem) => {
    const copyData = {
      title_en: pageItem.title_en || '', title_cz: pageItem.title_cz || '', title_de: pageItem.title_de || '',
      private: pageItem.private || false,
    };
    localStorage.setItem('admin_infopages_create', JSON.stringify(copyData));
    navigate('/admin/infopages/new');
  };

  const handleSubmit = async (formData) => {
    if (isCreating) {
      const result = await createPageApi.execute(formData);

      if (createPageApi.error) {
        toast.error(createPageApi.error || 'An error occurred while creating the page', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('Page created successfully!', {
          position: "bottom-right"
        });
        if (storageKey) localStorage.removeItem(storageKey);
        await fetchPages();
        navigate('/admin/infopages');
      }
    } else if (editingPage) {
      const result = await updatePageApi.execute({
        ...formData,
        page_id: editingPage.id
      });

      if (updatePageApi.error) {
        toast.error(updatePageApi.error || 'An error occurred while updating the page', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('Page updated successfully!', {
          position: "bottom-right"
        });
        if (storageKey) localStorage.removeItem(storageKey);
        await fetchPages();
        navigate('/admin/infopages');
      }
    }
  };

  const handleDelete = async (pageId) => {
    if (window.confirm('Are you sure you want to delete this page? All associated documents will also be deleted.')) {
      const result = await deletePageApi.execute({ page_id: pageId });
      if (deletePageApi.error) {
        toast.error(deletePageApi.error || 'An error occurred while deleting the page', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('Page deleted successfully!', {
          position: "bottom-right"
        });
        await fetchPages();
        navigate('/admin/infopages');
      }
    }
  };

  const getLocalizedField = (pageItem, fieldPrefix) => {
    const language = i18n.language;
    if (pageItem[`${fieldPrefix}_${language}`]) {
      return pageItem[`${fieldPrefix}_${language}`];
    }
    if (pageItem[`${fieldPrefix}_cz`]) {
      return pageItem[`${fieldPrefix}_cz`];
    }
    if (pageItem[`${fieldPrefix}_en`]) {
      return pageItem[`${fieldPrefix}_en`];
    }
    if (pageItem[`${fieldPrefix}_de`]) {
      return pageItem[`${fieldPrefix}_de`];
    }
    return '';
  };

  const pageFields = [
    {
      type: 'language_tabs',
      name: 'content_tabs',
      languages: ['en', 'cz', 'de'],
      fields: [
        { name: 'title', label: t('admin.fields.title'), type: 'text', required: false, placeholder: t('admin.placeholders.title') }
        //{ name: 'text', label: t('admin.fields.content'), type: 'markdown', required: false, placeholder: t('admin.placeholders.content') }
      ]
    },
    { name: 'private', label: t('admin.fields.membersOnly'), type: 'boolean', defaultValue: false }
  ];

  const isDetailView = id && !window.location.pathname.includes('/edit') && !window.location.pathname.endsWith('/new');

  if (isDetailView) {
    return <PageDetailView pageId={id} />;
  }

  if (isCreating || editingPage) {
    return (
      <div className="tab-container">
        <div className="tab-header">
          <h2 className="tab-title">{isCreating ? t('admin.pages.createTitle') : t('admin.pages.editTitle')}</h2>
          <p className="tab-description">
            {isCreating ? t('admin.pages.createDesc') : `${t('admin.pages.editDesc')}: ${getLocalizedField(editingPage, 'title')}`}
          </p>
        </div>

        <div className="edit-actions-top">
          <button className="cancel-btn-top" onClick={handleCancel}>
            {t('common.cancel')}
          </button>
          {editingPage && (
            <button className="copy-btn-top" onClick={() => handleCopy(editingPage)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              {t('common.copy', 'Copy')}
            </button>
          )}
          {editingPage && (
            <button
              className="delete-btn"
              onClick={() => handleDelete(editingPage.id)}
              disabled={deletePageApi.loading}
            >
              {deletePageApi.loading ? t('common.deleting') : t('admin.pages.delete')}
            </button>
          )}
        </div>

        <AdminEndpoint
          fields={pageFields}
          onSubmit={handleSubmit}
          onCancel={null}
          initialData={editingPage || {}}
          submitLabel={isCreating ? t('admin.pages.createBtn') : t('admin.pages.updateBtn')}
          isLoading={createPageApi.loading || updatePageApi.loading}
          storageKey={storageKey}
        />
      </div>
    );
  }

  return (
    <div className="tab-container">
      <div className="tab-header">
        <div>
          <h2 className="tab-title">{t('admin.pages.title')}</h2>
        </div>
        <button className="create-btn" onClick={handleCreate}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          {t('admin.pages.createBtn')}
        </button>
      </div>

      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder={t('admin.pages.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {listPagesApi.loading && allPages.length === 0 ? (
        <LoadingSpinner message={t('common.loading', 'Loading pages...')} />
      ) : allPages.length === 0 ? (
        <div className="empty-state">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p>{t('admin.pages.noPages')}</p>
          <button className="create-btn-secondary" onClick={handleCreate}>
            {t('admin.pages.createBtn')}
          </button>
        </div>
      ) : (
        <>
          <div className="items-grid" ref={gridRef} style={{ position: 'relative', minHeight: listPagesApi.loading ? '400px' : 'auto' }}>
            {listPagesApi.loading && allPages.length > 0 && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '12px'
              }}>
                <LoadingSpinner message={t('common.loading', 'Loading...')} />
              </div>
            )}
            {displayedPages.map(pageItem => (
              <div key={pageItem.id} className="item-card" onClick={() => handleEdit(pageItem)}>
                <div className="item-card-body">
                  <div className="item-id">#{pageItem.id}</div>
                  <h3 className="item-title">{getLocalizedField(pageItem, 'title') || 'Untitled Page'}</h3>
                  <div className="item-meta">
                    <span className="item-date">
                      {new Date(pageItem.created_at).toLocaleDateString()}
                    </span>
                    {pageItem.private && <span className="item-badge">Members Only</span>}
                  </div>
                </div>
                <div className="item-card-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="item-copy-btn" onClick={() => handleCopy(pageItem)} title="Duplicate">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                    Copy
                  </button>
                  <button className="item-delete-btn" onClick={() => handleDelete(pageItem.id)} title="Delete">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline strokeLinecap="round" strokeLinejoin="round" points="3 6 5 6 21 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </button>
              <span className="pagination-info">
                {t('common.page')} {currentPage} {t('common.of')} {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InfoPagesTab;
